import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, EffectCoverflow } from 'swiper/modules';
import { IconStar, IconQuote, IconArrowLeft, IconArrowRight, IconChevronDown, IconChevronUp, IconCheck } from './Icons';
import { useLanguage } from '../context/LanguageContext';
import useScrollReveal from '../hooks/useScrollReveal';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-coverflow';
import './Testimonials.css';

const MAX_TEXT_LENGTH = 280;

const Testimonials = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedIds, setExpandedIds] = useState([]);

  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const testimonials = [
    { id: 1, text: t.testimonials.testimonial1, name: "Papierdrachen", title: t.testimonials.role_kunde, company: "Papierdrachen", image: "/img/papierdrache.jpeg", rating: 5 },
    { id: 2, text: t.testimonials.testimonial2, name: "Jeremy Gebhardt", title: t.testimonials.role_fbaSeller, company: "Amazon FBA", image: "/img/testi_1.png", rating: 5 },
    { id: 3, text: t.testimonials.testimonial3, name: "Dr. Hari Krishnan", title: t.testimonials.role_ceo, company: "Heilpflanzenwohl AG", image: "/img/testi_3.png", rating: 5 },
    { id: 4, text: t.testimonials.testimonial4, name: "Martin Frost", title: t.testimonials.role_gruender, company: "Martin-Frost.de", image: "/img/testi_2.png", rating: 5 },
    { id: 5, text: t.testimonials.testimonial5, name: "Christoph Booms", title: t.testimonials.role_geschaeftsfuehrer, company: "Booms", image: "/img/testimonials/christoph booms.jpeg", rating: 5 },
    { id: 6, text: t.testimonials.testimonial6, name: "Lucas Beier", title: t.testimonials.role_gruender, company: "Brandcircle", image: "/img/testimonials/Lucas Beier.jpeg", rating: 5 },
    { id: 7, text: t.testimonials.testimonial7, name: "Patrick Hild", title: t.testimonials.role_geschaeftsfuehrer, company: "Lucky Treats", image: "/img/testimonials/Patrick Hild.jpeg", rating: 5 },
    { id: 8, text: t.testimonials.testimonial8, name: "Flavio", title: t.testimonials.role_gruender, company: "Natural Athlet", image: "/img/testimonials/flavio.webp", rating: 5 },
    { id: 9, text: t.testimonials.testimonial9, name: "Christof Jugel", title: t.testimonials.role_gruender, company: "Nyfter", image: "/img/testimonials/Christof Jugel Nyfter.webp", rating: 5 },
    { id: 10, text: t.testimonials.testimonial10, name: "Andreas Oswald", title: t.testimonials.role_geschaeftsfuehrer, company: "Oswald Lederwaren", image: "/img/testimonials/oswald bild.jpeg", rating: 5 },
  ];

  const renderText = (testimonial) => {
    const isExpanded = expandedIds.includes(testimonial.id);
    const needsTruncation = testimonial.text.length > MAX_TEXT_LENGTH;
    
    if (!needsTruncation) {
      return <p className="testimonials-fox__text">"{testimonial.text}"</p>;
    }

    const truncatedText = isExpanded ? testimonial.text : testimonial.text.substring(0, MAX_TEXT_LENGTH) + '...';

    return (
      <div className="testimonials-fox__text-wrapper">
        <p className="testimonials-fox__text">"{truncatedText}"</p>
        <button className="testimonials-fox__expand-btn" onClick={(e) => toggleExpand(testimonial.id, e)}>
          {isExpanded ? t.testimonials.readLess : t.testimonials.readMore}
          {isExpanded ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
        </button>
      </div>
    );
  };

  const headerReveal = useScrollReveal({ threshold: 0.15 });
  const sliderReveal = useScrollReveal({ threshold: 0.1 });
  const trustReveal = useScrollReveal({ threshold: 0.2 });

  return (
    <section className="testimonials-fox" id="testimonials">
      <div className="testimonials-fox__bg-grid"></div>
      <div className="testimonials-fox__bg-orb testimonials-fox__bg-orb--1"></div>
      <div className="testimonials-fox__bg-orb testimonials-fox__bg-orb--2"></div>
      
      <div className="testimonials-fox__container">
        <div ref={headerReveal.ref} className={`testimonials-fox__header reveal ${headerReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <div className="testimonials-fox__avatar-stack">
            <img src="/img/testimonials/Patrick Hild.jpeg" alt="" className="testimonials-fox__avatar-stack-img" loading="lazy" />
            <img src="/img/testimonials/Christof Jugel Nyfter.webp" alt="" className="testimonials-fox__avatar-stack-img" loading="lazy" />
            <img src="/img/testimonials/flavio.webp" alt="" className="testimonials-fox__avatar-stack-img" loading="lazy" />
            <img src="/img/testimonials/oswald bild.jpeg" alt="" className="testimonials-fox__avatar-stack-img" loading="lazy" />
            <img src="/img/testimonials/Lucas Beier.jpeg" alt="" className="testimonials-fox__avatar-stack-img" loading="lazy" />
          </div>
          <h2 className="testimonials-fox__title">
            {t.testimonials.title} <span className="testimonials-fox__title-gradient">{t.testimonials.titleHighlight}</span>
          </h2>
          <p className="testimonials-fox__subtitle">{t.testimonials.subtitle}</p>
        </div>

        <div ref={sliderReveal.ref} className={`testimonials-fox__slider-wrapper reveal--fade ${sliderReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <Swiper
            modules={[Navigation, Pagination, EffectCoverflow]}
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView="auto"
            initialSlide={0}
            slideToClickedSlide={true}
            coverflowEffect={{ rotate: 0, stretch: 50, depth: 120, modifier: 1, slideShadows: false }}
            breakpoints={{
              0: { coverflowEffect: { rotate: 0, stretch: 0, depth: 0, modifier: 1, slideShadows: false }, spaceBetween: 16 },
              768: { coverflowEffect: { rotate: 0, stretch: 50, depth: 120, modifier: 1, slideShadows: false }, spaceBetween: 0 },
            }}
            pagination={{ clickable: true }}
            navigation={{ nextEl: '.testimonials-fox__nav--next', prevEl: '.testimonials-fox__nav--prev' }}
            loop={true}
            loopAdditionalSlides={2}
            speed={700}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            className="testimonials-fox__swiper"
          >
            {testimonials.map((testimonial, index) => (
              <SwiperSlide key={testimonial.id} className="testimonials-fox__slide">
                <div className={`testimonials-fox__card ${activeIndex === index ? 'testimonials-fox__card--active' : ''}`}>
                  <div className="testimonials-fox__card-inner">
                    <div className="testimonials-fox__quote-icon"><IconQuote size={24} /></div>
                    <div className="testimonials-fox__stars">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="testimonials-fox__star"><IconStar size={16} filled={true} /></span>
                      ))}
                    </div>
                    {renderText(testimonial)}
                    <div className="testimonials-fox__author">
                      <div className="testimonials-fox__avatar-wrapper">
                        <img src={testimonial.image} alt={testimonial.name} className="testimonials-fox__avatar" width={56} height={56} loading="lazy" decoding="async" />
                        <span className="testimonials-fox__avatar-ring"></span>
                      </div>
                      <div className="testimonials-fox__author-info">
                        <p className="testimonials-fox__name">{testimonial.name}</p>
                        <p className="testimonials-fox__role">{testimonial.title} <span>•</span> {testimonial.company}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          <div className="testimonials-fox__navigation">
            <button className="testimonials-fox__nav testimonials-fox__nav--prev" aria-label={t.testimonials.prevLabel}><IconArrowLeft size={18} /></button>
            <button className="testimonials-fox__nav testimonials-fox__nav--next" aria-label={t.testimonials.nextLabel}><IconArrowRight size={18} /></button>
          </div>
        </div>

        <div ref={trustReveal.ref} className={`testimonials-fox__trust reveal ${trustReveal.isRevealed ? 'reveal--visible' : ''}`}>
          <div className="testimonials-fox__trust-item">
            <div className="testimonials-fox__trust-avatars">
              <img src="/img/testimonials/Patrick Hild.jpeg" alt="" className="testimonials-fox__trust-avatar" loading="lazy" />
              <img src="/img/testimonials/Christof Jugel Nyfter.webp" alt="" className="testimonials-fox__trust-avatar" loading="lazy" />
              <img src="/img/testimonials/flavio.webp" alt="" className="testimonials-fox__trust-avatar" loading="lazy" />
              <img src="/img/testimonials/oswald bild.jpeg" alt="" className="testimonials-fox__trust-avatar" loading="lazy" />
              <img src="/img/testimonials/Lucas Beier.jpeg" alt="" className="testimonials-fox__trust-avatar" loading="lazy" />
            </div>
            <span className="testimonials-fox__trust-number">{t.testimonials.trustCount}</span>
            <span className="testimonials-fox__trust-label">{t.testimonials.trustLabel}</span>
          </div>
          <div className="testimonials-fox__trust-divider"></div>
          <div className="testimonials-fox__trust-item">
            <div className="testimonials-fox__trust-stars">
              {[...Array(5)].map((_, i) => (<IconStar key={i} size={16} filled={true} />))}
            </div>
            <span className="testimonials-fox__trust-number">{t.testimonials.trustRating}</span>
            <span className="testimonials-fox__trust-label">{t.testimonials.trustRatingLabel}</span>
          </div>
          <div className="testimonials-fox__trust-divider"></div>
          <div className="testimonials-fox__trust-item">
            <span className="testimonials-fox__trust-check"><IconCheck size={22} /></span>
            <span className="testimonials-fox__trust-number">{t.testimonials.trustRecommend}</span>
            <span className="testimonials-fox__trust-label">{t.testimonials.trustRecommendLabel}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
